import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SourceKind } from '@quizdb/common';
import { useLazyGetJobMaterialPageQuery } from '../job/jobEndpoints';
import { problemCode } from '../api/problemCode';

/** Props for `MaterialPageLink`. */
export interface MaterialPageLinkProps {
  jobId?: string;
  page: number;
  kind?: SourceKind;
}

/**
 * Cites the page a draft was taken from: "PDF p. N" for a PDF, or "Photo N" for a photo set's
 * page, absent `kind` meaning `pdf` (QZ-D93). When the task has a `jobId`, also shows an
 * Open/Hide toggle, in `OpenMaterial`, that expands the material's text for that page below
 * the citation and collapses it again, for both kinds alike (PTX-D12). Without a `jobId`, the
 * RTK hook never runs and the label renders without a store `Provider`.
 */
export const MaterialPageLink = ({ jobId, page, kind }: MaterialPageLinkProps) => {
  const { t } = useTranslation();

  return (
    <>
      <span>
        {kind === 'photos' ? t('task.source.photo', { page }) : t('task.source.pdfPage', { page })}
      </span>
      {jobId && (
        <>
          {' · '}
          <OpenMaterial jobId={jobId} page={page} />
        </>
      )}
    </>
  );
};

/** Props for `OpenMaterial`. */
interface OpenMaterialProps {
  jobId: string;
  page: number;
}

/**
 * Toggles the job material's text for `page` open below the citation (PTX-D12). Opening
 * fetches the page; while open, a `page` change (e.g. a draft's source page edited in
 * `TaskForm`) refetches for the new page. An expired material (QZ-D71) replaces the button
 * with a fixed notice, since there is nothing left to retry; any other failure keeps the
 * button so a click can retry.
 */
const OpenMaterial = ({ jobId, page }: OpenMaterialProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const [trigger, { data, error, isFetching }] = useLazyGetJobMaterialPageQuery();

  useEffect(() => {
    if (open) {
      void trigger({ jobId, page });
    }
  }, [open, jobId, page, trigger]);

  if (error && problemCode(error) === 'MaterialExpired') {
    return <span className="qz-muted">{t('task.source.materialExpired')}</span>;
  }

  // Once a fetch has failed, `open` no longer tracks "closed vs. showing the panel": the
  // only way out of the error is a successful retry, so a click always retriggers the fetch
  // for the same arguments, which `open` alone (unchanged) would not do via the effect above.
  const handleClick = () => {
    if (error) {
      void trigger({ jobId, page });
    } else if (open) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  };

  const showingPanel = open && !error;
  const panelRendered = showingPanel && !!data && !isFetching && data.page === page;

  return (
    <>
      <button
        type="button"
        className="btn btn-link btn-sm p-0 qz-small"
        disabled={isFetching}
        aria-expanded={showingPanel}
        // Only while the panel is in the DOM, so the reference never dangles.
        aria-controls={panelRendered ? panelId : undefined}
        onClick={handleClick}
      >
        {showingPanel ? t('task.source.hide') : t('task.source.open')}
      </button>
      {error && <span className="text-danger"> {t('error.unknown')}</span>}
      {panelRendered && (
        <div
          id={panelId}
          className="border rounded p-2 mt-2 overflow-auto"
          style={{ maxHeight: '20rem', whiteSpace: 'pre-wrap' }}
        >
          {data?.text}
        </div>
      )}
    </>
  );
};

export default MaterialPageLink;
